export class ApiRequestError extends Error {
    statusCode;
    responseBody;
    constructor(message, statusCode, responseBody) {
        super(message);
        this.statusCode = statusCode;
        this.responseBody = responseBody;
        this.name = 'ApiRequestError';
    }
    /** Returns the human-readable message from the API error envelope when present. */
    toUserMessage() {
        if (this.responseBody === '')
            return this.message;
        try {
            const parsed = JSON.parse(this.responseBody);
            if (typeof parsed.message === 'string' && parsed.message.trim().length > 0)
                return parsed.message;
            if (typeof parsed.error === 'string' && parsed.error.trim().length > 0)
                return parsed.error;
        }
        catch {
            // Fall through to default.
        }
        return this.message;
    }
}
/**
 * Builds the single HTTP entry point an SPA uses to reach the API.
 *
 * Each app owns its own base URL, so the client is constructed per app rather
 * than exported as a singleton; everything else — bearer token, JSON encoding,
 * error envelope handling — is identical across the member PWA, the organizer
 * admin and the internal console, and lives here.
 */
export function createApiRequest({ resolveBaseUrl, resolveAuthToken, renewal, onUnauthenticated, }) {
    /**
     * **The one place this platform's client code attaches a credential**, and the one place a
     * refusal is reported to the host. Both `apiRequest` and `apiRequest.download` go through it.
     *
     * It exists because they did not. Seven download call sites read the stored token themselves
     * and built their own `Authorization` header — they had to, because the JSON path above
     * consumes the body as text and loses both the bytes and the `Content-Disposition` name. Every
     * criterion on `PLT-014` is a claim about what happens when a request is refused, and none of
     * them was true of those seven: they threw `export failed (401)` and nothing ever re-checked
     * the session. See `docs/decisions/051-…` §1.
     *
     * Returned as a raw `Response` so each caller decides what the body *is*. Nothing above this
     * function reads a token, and nothing below it decides what a status means.
     */
    async function send(path, spec) {
        const url = buildUrl(resolveBaseUrl(), path, spec.searchParams);
        let serializedBody;
        if (spec.body !== undefined) {
            serializedBody = JSON.stringify(spec.body);
        }
        /** One attempt, with one credential. Serialised once above so a retry re-sends the same bytes. */
        async function attempt(token) {
            const headers = new Headers();
            if (spec.accept !== null)
                headers.set('Accept', spec.accept);
            if (token !== null && token.length > 0) {
                headers.set('Authorization', `Bearer ${token}`);
            }
            if (serializedBody !== undefined)
                headers.set('Content-Type', 'application/json');
            return fetch(url, {
                method: spec.method,
                headers,
                cache: 'no-store',
                ...(serializedBody === undefined ? {} : { body: serializedBody }),
                ...(spec.signal === undefined ? {} : { signal: spec.signal }),
            });
        }
        // **Renew before sending, not after being refused** (`ADR 051` §2). The access token's own
        // `exp` is read locally, which costs nothing, and a request whose credential has already
        // expired is not sent at all — the renewal replaces the round trip rather than following it.
        // Sumit's suggestion, with the one correction the ADR records: the answer to "expired" is
        // *renew*, not *redirect*.
        const held = resolveAuthToken?.() ?? null;
        const token = renewal === undefined ? held : await renewal.credentialFitToSend(held);
        const response = await attempt(token);
        // **A 401 that arrives anyway gets one renewal and one retry.** The clock was wrong, or the
        // token was revoked between the `exp` check and the server reading it. `ADR 051` §2 bounds
        // this at one: a second 401 is not a stale token, it is a session that is over, and it takes
        // the path below.
        //
        // Guarded on a credential having been sent, because a 401 on an anonymous request is a public
        // route's refusal and has nothing to renew. Guarded on 401 alone, not 403: a 403 means the
        // token *was* accepted and the request refused on its merits, so a fresh token would be
        // refused identically.
        //
        // `renewed === token` is the do-nothing case, and it is checked rather than assumed: with
        // refresh-token rotation off, a renewal that quietly failed could hand back the same string,
        // and retrying an identical request against an identical credential is a second round trip
        // for a certain second 401.
        if (response.status === 401 && token !== null && token.length > 0 && renewal !== undefined) {
            const renewed = await renewal.renewAfterRefusal();
            if (renewed !== null && renewed.length > 0 && renewed !== token) {
                const retried = await attempt(renewed);
                if (retried.status === 401 || retried.status === 403)
                    await reportRefusal(retried);
                return retried;
            }
        }
        // **A 401 on a request that carried a token means the token was refused.**
        //
        // Same contract as `createAuthClient`, which has treated this as the "session ended"
        // signal all along — this client simply never had it, so an expired session behaved
        // differently depending on which client noticed first. Navigating the console after expiry
        // left every page on its skeletons: the domain calls 401'd, the cached user stayed
        // non-null, and only a browser refresh re-ran `/auth/me`.
        //
        // **It is not guarded on a token having been sent, and that guard is why this shipped
        // broken twice.** The rule was "a 401 with no token is a public route's refusal or a wrong
        // password, not a session ending" — true of a sign-in screen and false of a console that is
        // already rendering somebody's session. Sumit reproduced it on the admin ledger on
        // 2026-09-02: the token had gone from storage, every domain call went out without it and
        // came back 401, and this line stayed silent because no token was sent. The screen 401'd
        // forever and no `/auth/me` was ever asked.
        //
        // The question "is this a session ending?" cannot be answered from the request. It is
        // answered by whether the app believes it has a session, which only the host knows — so the
        // host is asked every time and decides. See `onSessionRecheckRequested` in `@h2hspark/auth-ui`:
        // no session and no token is a signed-out visitor, and it still does nothing.
        // **A 401 and a 403 both ask the same question, and neither answers it.**
        //
        // A 401 says the token was refused. A 403 says this token may not do this — and on the
        // organizer console that is what a decayed session looks like, because
        // `require-organizer-membership.ts` throws `PermissionDeniedError` when it cannot resolve a
        // membership for the session it was given. Reading 403 as proof the session is healthy was
        // wrong, and it was worse than saying nothing: every card on the dashboard refused, each one
        // raised "you're not authorised", and there was no way forward because nothing re-checked.
        //
        // So both ask the host to re-verify, and `/auth/me` decides. A session that has ended
        // answers 401 there, is cleared, and the browser goes to sign-in. A session that is alive
        // means the 403 really was about permission, and the message is right. The client never
        // decides which; it only knows it has a reason to doubt.
        //
        // **One refusal does not go to `/auth/me`, and this transport still does not decide it.** A
        // suspended account's 403 carries `ACCOUNT_SUSPENDED`, and the authority would answer "alive"
        // about a token that is still valid — so the *code* travels with the status (`PLT-014` AC-7)
        // and the host branches on it. The branch is in `@h2hspark/auth-ui`, where every other
        // decision about what a refusal means already lives.
        if (response.status === 401 || response.status === 403) {
            await reportRefusal(response);
        }
        return response;
    }
    /**
     * Hands a refusal to the host with the code the API sent beside it.
     *
     * The body is read off a **clone**: the caller is about to read the original for its error
     * envelope, and a `Response` body can be read once. Reading it here rather than in the two
     * callers keeps `send` the one place a refusal is reported, which `PLT-014` AC-6 depends on.
     */
    async function reportRefusal(response) {
        if (onUnauthenticated === undefined)
            return;
        onUnauthenticated({ status: response.status, errorCode: await errorCodeOf(response.clone()) });
    }
    async function apiRequest(path, options = {}) {
        const { method = 'GET', body, searchParams, signal } = options;
        const response = await send(path, {
            method,
            accept: 'application/json',
            body,
            searchParams,
            signal,
        });
        const responseText = await response.text();
        if (!response.ok) {
            throw new ApiRequestError(`Request failed with status ${response.status}.`, response.status, responseText);
        }
        if (responseText === '')
            return undefined;
        try {
            return JSON.parse(responseText);
        }
        catch {
            throw new ApiRequestError('The server returned data that is not valid JSON.', response.status, responseText);
        }
    }
    /**
     * **A download, through the same transport as everything else** (`PLT-014` AC-6, `ADR 051` §1).
     *
     * The bytes and the server's filename come back; what to do with them is the caller's. What is
     * *not* the caller's, any more, is the credential and what a refusal means — both are `send`'s,
     * so a download now re-checks the session exactly as a JSON read does, and a renewal that lands
     * in the transport reaches these surfaces without any of them changing again.
     *
     * The error is an {@link ApiRequestError} rather than the seven bespoke `export failed (401)`
     * strings it replaces, so a caller can read the status without parsing a sentence.
     */
    async function download(path, options = {}) {
        const { method = 'GET', body, searchParams, accept, signal } = options;
        const response = await send(path, {
            method,
            accept: accept ?? null,
            body,
            searchParams,
            signal,
        });
        if (!response.ok) {
            // An error envelope is JSON even when the request asked for bytes, so it is readable — but
            // a body that fails to read must not mask the status the caller needs.
            const responseText = await response.text().catch(() => '');
            throw new ApiRequestError(`Request failed with status ${response.status}.`, response.status, responseText);
        }
        return {
            blob: await response.blob(),
            filename: filenameFrom(response.headers.get('content-disposition')),
            contentType: response.headers.get('content-type'),
        };
    }
    return Object.assign(apiRequest, { download });
}
/**
 * The `errorCode` in an API error envelope, or null when there is none to read.
 *
 * Null for an empty body, a body that is not JSON, and a JSON body with no string `errorCode` —
 * the API Gateway authorizer's `{"message":"Unauthorized"}` is the everyday case of the last. None
 * of those is an error here: the status alone was always enough to ask the host, and it still is.
 */
async function errorCodeOf(response) {
    try {
        const parsed = JSON.parse(await response.text());
        return typeof parsed.errorCode === 'string' && parsed.errorCode.length > 0
            ? parsed.errorCode
            : null;
    }
    catch {
        return null;
    }
}
/**
 * The filename the server put in `Content-Disposition`, or null.
 *
 * `@h2hspark/export` always emits `attachment; filename="…"`; the unquoted form is accepted
 * because the header's grammar allows it and a client that only reads one form would silently
 * fall back to its own name for a file the server had already named.
 */
function filenameFrom(disposition) {
    if (disposition === null)
        return null;
    const quoted = /filename="([^"]+)"/u.exec(disposition);
    if (quoted?.[1] !== undefined)
        return quoted[1];
    const unquoted = /filename=([^;]+)/u.exec(disposition);
    return unquoted?.[1]?.trim() ?? null;
}
function buildUrl(baseUrl, path, searchParams) {
    // The base URL is a *prefix*, not merely an origin — the deployed API lives behind an API
    // Gateway stage, so the base is `https://<id>.execute-api…/v1`. `new URL(path, base)` with an
    // absolute path REPLACES the base's entire path: `/api/users` against `…/v1` resolved to
    // `…/api/users`, silently dropping the stage. API Gateway answered 403/404 with no CORS
    // headers, which a browser reports as a CORS error — so every request through this client
    // failed on the deployed environment while auth-ui's (which joins relatively) worked, and the
    // two half-broken clients looked like one impossible bug. Resolving the path *relatively*
    // against the base-with-trailing-slash keeps the stage.
    const url = new URL(path.startsWith('/') ? path.slice(1) : path, baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`);
    if (searchParams !== undefined) {
        for (const [key, value] of Object.entries(searchParams)) {
            if (value === undefined)
                continue;
            url.searchParams.set(key, String(value));
        }
    }
    return url.toString();
}
//# sourceMappingURL=api-client.js.map